import { motion } from "framer-motion";
import { TiltCard } from "./TiltCard";
import "./Projects.css";

const EASE = [0.16, 1, 0.3, 1];

const projects = [
  {
    title: "BizEdge Strategic Origination",
    category: "Enterprise platform",
    description:
      "Led delivery of Westpac's flagship business banking platform serving 10k+ customers with 1.5M+ daily transactions. Micro-frontend architecture using Single-Spa with Java Spring Boot microservices on AWS.",
    tech: ["Java", "Spring Boot", "ReactJS", "Single-Spa", "AWS", "GenAI"],
    accent: "purple",
    github: "https://github.com/Deepakhacker-lab",
    highlights: ["1.5M+ transactions/day", "32% faster approvals", "10k+ business customers"],
    icon: "01",
  },
  {
    title: "AI Code Review Bot",
    category: "AI automation",
    description:
      "Automated AI-driven code reviewer that scans pull requests, identifies defects, flags performance issues, and generates improvement suggestions using the OpenAI Assistants API with custom LLM policies.",
    tech: ["Python", "OpenAI API", "GitHub Actions", "LangGraph", "FastAPI"],
    accent: "cyan",
    github: "https://github.com/Deepakhacker-lab",
    highlights: ["Custom LLM policies", "Auto PR commenting", "Insecure-code detection"],
    icon: "02",
  },
  {
    title: "AI Test Generation Pipeline",
    category: "Developer tooling",
    description:
      "AI-powered pipeline that analyses code diffs and automatically produces JUnit/Jest unit test stubs. Integrated with GitHub Actions to trigger on every pull request, improving coverage and reducing workload.",
    tech: ["GitHub Actions", "OpenAI", "JUnit", "Jest", "Python"],
    accent: "pink",
    github: "https://github.com/Deepakhacker-lab",
    highlights: ["Auto test stubs", "PR-triggered pipeline", "JUnit & Jest support"],
    icon: "03",
  },
  {
    title: "Digital Credit Risk Assessment",
    category: "Cloud data system",
    description:
      "AWS Cloud Native credit risk evaluation system at Westpac with batch and streaming data pipelines. Built using Apache Beam and NodeJS with full CI/CD and Splunk observability.",
    tech: ["AWS", "Apache Beam", "NodeJS", "Jenkins", "Splunk", "Docker"],
    accent: "green",
    github: "https://github.com/Deepakhacker-lab",
    highlights: ["Cloud-native architecture", "Streaming pipelines", "Splunk observability"],
    icon: "04",
  },
];

const cardVariants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: EASE } },
};

function ProjectVisual({ accent, index }) {
  return (
    <div className={`project-card__visual project-card__visual--${accent}`}>
      <div className="project-card__visual-grid" />
      <div className="project-card__mock">
        <div className="project-card__mock-top">
          <span />
          <span />
          <span />
        </div>
        <div className="project-card__mock-body">
          <div className="project-card__mock-main">
            <i />
            <i />
            <i />
          </div>
          <div className="project-card__mock-side">
            <b />
            <b />
            <b />
          </div>
        </div>
      </div>
      <div className="project-card__visual-badge">0{index + 1}</div>
    </div>
  );
}

export function Projects() {
  return (
    <section className="projects section" id="projects">
      <div className="section-container">

        <motion.div
          className="projects__header"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <span className="section-label">Portfolio</span>
          <h2 className="section-title">Selected work with measurable outcomes</h2>
          <p className="section-subtitle">
            A focused set of products and systems showing scale, interface
            craft, automation, and cloud-native delivery.
          </p>
        </motion.div>

        {/* Staggered card entrance — 0.1s delay per card per UI Pro Max stagger-sequence rule */}
        <motion.div
          className="projects__grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          {projects.map((project, i) => (
            <motion.div key={i} variants={cardVariants} style={{ height: "100%" }}>
              <TiltCard
                className={`project-card project-card--${project.accent}`}
                maxRotation={8}
                style={{ height: "100%" }}
              >
                <ProjectVisual accent={project.accent} index={i} />
                <div className="project-card__top-bar" />
                <div className="project-card__header">
                  <div>
                    <span className="project-card__eyebrow">{project.category}</span>
                  </div>
                  <div className="project-card__links">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-card__link"
                      aria-label="GitHub"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <h3 className="project-card__title">{project.title}</h3>
                <p className="project-card__desc">{project.description}</p>

                <ul className="project-card__highlights">
                  {project.highlights.map((h, j) => (
                    <li key={j} className="project-card__highlight">
                      <span className="project-card__check">✓</span>
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="project-card__tech">
                  {project.tech.map((t) => (
                    <span className="project-card__tech-tag" key={t}>{t}</span>
                  ))}
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="projects__cta"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <p>See more on GitHub</p>
          <a
            href="https://github.com/Deepakhacker-lab"
            target="_blank"
            rel="noopener noreferrer"
            className="projects__github-link"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            View GitHub Profile
          </a>
        </motion.div>

      </div>
    </section>
  );
}
